import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import type {
  Customer,
  Quote,
  Project,
  Task,
  Purchase,
  Receivable,
  Collection,
  Payment,
  ProjectFinance,
  ServiceRecord,
  FileItem,
  AppNotification,
  CalendarEvent,
} from '@/types';

const num = (v: unknown) => Number(v ?? 0);

type Row = Record<string, unknown>;

function mapCustomer(r: Row): Customer {
  return {
    id: String(r.id),
    name: String(r.name ?? ''),
    contactPerson: String(r.contact_person ?? ''),
    phone: String(r.phone ?? ''),
    email: String(r.email ?? ''),
    city: String(r.city ?? ''),
    sector: String(r.sector ?? ''),
    activeProjects: num(r.active_projects),
    openQuotes: num(r.open_quotes),
    totalSales: num(r.total_sales),
    receivable: num(r.receivable),
    lastContact: String(r.last_contact ?? ''),
    address: String(r.address ?? ''),
    notes: String(r.notes ?? ''),
  };
}

function mapQuote(r: Row): Quote {
  return {
    id: String(r.id),
    number: String(r.number ?? ''),
    date: String(r.date ?? ''),
    customerId: String(r.customer_id ?? ''),
    projectName: String(r.project_name ?? ''),
    category: String(r.category ?? ''),
    amount: num(r.amount),
    estimatedCost: num(r.estimated_cost),
    estimatedProfit: num(r.estimated_profit),
    validityDate: String(r.validity_date ?? ''),
    status: String(r.status ?? 'Taslak') as Quote['status'],
    lastContact: String(r.last_contact ?? ''),
    nextFollowUp: String(r.next_follow_up ?? ''),
    description: String(r.description ?? ''),
    taxStatus: String(r.tax_status ?? ''),
    paymentPlan: String(r.payment_plan ?? ''),
    deliveryTime: String(r.delivery_time ?? ''),
    technicalNotes: String(r.technical_notes ?? ''),
    internalNote: String(r.internal_note ?? ''),
  };
}

function mapProject(r: Row): Project {
  return {
    id: String(r.id),
    name: String(r.name ?? ''),
    customerId: String(r.customer_id ?? ''),
    category: String(r.category ?? ''),
    responsible: String(r.responsible ?? ''),
    startDate: String(r.start_date ?? ''),
    plannedDelivery: String(r.planned_delivery ?? ''),
    status: String(r.status ?? 'Devam Ediyor') as Project['status'],
    progress: num(r.progress),
    salesAmount: num(r.sales_amount),
    collected: num(r.collected),
    cost: num(r.cost),
    currentStage: String(r.current_stage ?? ''),
    blockedStage: r.blocked_stage ? String(r.blocked_stage) : undefined,
    description: String(r.description ?? ''),
  };
}

function mapTask(r: Row): Task {
  return {
    id: String(r.id),
    title: String(r.title ?? ''),
    customerId: String(r.customer_id ?? ''),
    projectId: String(r.project_id ?? ''),
    assignee: String(r.assignee ?? ''),
    startDate: String(r.start_date ?? ''),
    dueDate: String(r.due_date ?? ''),
    priority: String(r.priority ?? 'Normal') as Task['priority'],
    status: String(r.status ?? 'Yapılacak') as Task['status'],
  };
}

function mapPurchase(r: Row): Purchase {
  return {
    id: String(r.id),
    projectId: String(r.project_id ?? ''),
    supplier: String(r.supplier ?? ''),
    material: String(r.material ?? ''),
    brandModel: String(r.brand_model ?? ''),
    quantity: num(r.quantity),
    unitPrice: num(r.unit_price),
    total: num(r.total),
    orderDate: String(r.order_date ?? ''),
    expectedDelivery: String(r.expected_delivery ?? ''),
    paymentStatus: String(r.payment_status ?? ''),
    materialStatus: String(r.material_status ?? ''),
  };
}

function mapReceivable(r: Row): Receivable {
  return {
    id: String(r.id),
    customerId: String(r.customer_id ?? ''),
    projectId: String(r.project_id ?? ''),
    totalSales: num(r.total_sales),
    collected: num(r.collected),
    remaining: num(r.remaining),
    dueDate: String(r.due_date ?? ''),
    status: String(r.status ?? ''),
  };
}

function mapCollection(r: Row): Collection {
  return {
    id: String(r.id),
    date: String(r.date ?? ''),
    customerId: String(r.customer_id ?? ''),
    projectId: String(r.project_id ?? ''),
    paymentType: String(r.payment_type ?? ''),
    amount: num(r.amount),
    description: String(r.description ?? ''),
  };
}

function mapPayment(r: Row): Payment {
  return {
    id: String(r.id),
    date: String(r.date ?? ''),
    supplier: String(r.supplier ?? ''),
    projectId: String(r.project_id ?? ''),
    category: String(r.category ?? ''),
    amount: num(r.amount),
    description: String(r.description ?? ''),
  };
}

function mapProjectFinance(r: Row): ProjectFinance {
  return {
    projectId: String(r.project_id ?? ''),
    salesAmount: num(r.sales_amount),
    collected: num(r.collected),
    remaining: num(r.remaining),
    materialCost: num(r.material_cost),
    otherExpenses: num(r.other_expenses),
    totalCost: num(r.total_cost),
    grossProfit: num(r.gross_profit),
    profitRate: num(r.profit_rate),
  };
}

function mapService(r: Row): ServiceRecord {
  return {
    id: String(r.id),
    customerId: String(r.customer_id ?? ''),
    projectId: String(r.project_id ?? ''),
    requestDate: String(r.request_date ?? ''),
    issue: String(r.issue ?? ''),
    priority: String(r.priority ?? 'Normal') as ServiceRecord['priority'],
    assignee: String(r.assignee ?? ''),
    plannedDate: String(r.planned_date ?? ''),
    result: String(r.result ?? ''),
    warranty: String(r.warranty ?? 'Garanti') as ServiceRecord['warranty'],
    fee: num(r.fee),
    status: String(r.status ?? 'Yeni Talep') as ServiceRecord['status'],
  };
}

function mapFile(r: Row): FileItem {
  return {
    id: String(r.id),
    name: String(r.name ?? ''),
    category: String(r.category ?? ''),
    relatedType: String(r.related_type ?? '') as FileItem['relatedType'],
    relatedId: String(r.related_id ?? ''),
    size: String(r.size ?? ''),
    uploadedDate: String(r.uploaded_date ?? ''),
    storagePath: String(r.storage_path ?? ''),
  };
}

function mapNotification(r: Row): AppNotification {
  return {
    id: String(r.id),
    text: String(r.text ?? ''),
    type: String(r.type ?? 'info') as AppNotification['type'],
    date: String(r.date ?? ''),
    read: Boolean(r.read),
  };
}

function mapCalendarEvent(r: Row): CalendarEvent {
  return {
    id: String(r.id),
    date: String(r.date ?? ''),
    type: String(r.type ?? ''),
    title: String(r.title ?? ''),
    related: String(r.related ?? ''),
  };
}

interface DataContextValue {
  customers: Customer[];
  quotes: Quote[];
  projects: Project[];
  tasks: Task[];
  purchases: Purchase[];
  receivables: Receivable[];
  collections: Collection[];
  payments: Payment[];
  projectFinances: ProjectFinance[];
  serviceRecords: ServiceRecord[];
  files: FileItem[];
  notifications: AppNotification[];
  calendarEvents: CalendarEvent[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  customerName: (id: string) => string;
  projectName: (id: string) => string;
  customerById: (id: string) => Customer | undefined;
  projectById: (id: string) => Project | undefined;
}

const DataContext = createContext<DataContextValue | undefined>(undefined);

export function DataProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [receivables, setReceivables] = useState<Receivable[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [projectFinances, setProjectFinances] = useState<ProjectFinance[]>([]);
  const [serviceRecords, setServiceRecords] = useState<ServiceRecord[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all([
        supabase.from('3dmachine_customers').select('*'),
        supabase.from('3dmachine_quotes').select('*'),
        supabase.from('3dmachine_projects').select('*'),
        supabase.from('3dmachine_tasks').select('*'),
        supabase.from('3dmachine_purchases').select('*'),
        supabase.from('3dmachine_receivables').select('*'),
        supabase.from('3dmachine_collections').select('*'),
        supabase.from('3dmachine_payments').select('*'),
        supabase.from('3dmachine_project_finances').select('*'),
        supabase.from('3dmachine_service_records').select('*'),
        supabase.from('3dmachine_files').select('*'),
        supabase.from('3dmachine_notifications').select('*'),
        supabase.from('3dmachine_calendar_events').select('*'),
      ]);

      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;

      setCustomers((results[0].data ?? []).map(mapCustomer));
      setQuotes((results[1].data ?? []).map(mapQuote));
      setProjects((results[2].data ?? []).map(mapProject));
      setTasks((results[3].data ?? []).map(mapTask));
      setPurchases((results[4].data ?? []).map(mapPurchase));
      setReceivables((results[5].data ?? []).map(mapReceivable));
      setCollections((results[6].data ?? []).map(mapCollection));
      setPayments((results[7].data ?? []).map(mapPayment));
      setProjectFinances((results[8].data ?? []).map(mapProjectFinance));
      setServiceRecords((results[9].data ?? []).map(mapService));
      setFiles((results[10].data ?? []).map(mapFile));
      setNotifications((results[11].data ?? []).map(mapNotification));
      setCalendarEvents((results[12].data ?? []).map(mapCalendarEvent));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Veri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      load();
    } else {
      setCustomers([]);
      setQuotes([]);
      setProjects([]);
      setTasks([]);
      setPurchases([]);
      setReceivables([]);
      setCollections([]);
      setPayments([]);
      setProjectFinances([]);
      setServiceRecords([]);
      setFiles([]);
      setNotifications([]);
      setCalendarEvents([]);
    }
  }, [user, load]);

  const customerName = useCallback(
    (id: string) => customers.find((c) => c.id === id)?.name ?? id,
    [customers],
  );
  const projectName = useCallback(
    (id: string) => projects.find((p) => p.id === id)?.name ?? id,
    [projects],
  );
  const customerById = useCallback(
    (id: string) => customers.find((c) => c.id === id),
    [customers],
  );
  const projectById = useCallback(
    (id: string) => projects.find((p) => p.id === id),
    [projects],
  );

  const value = useMemo<DataContextValue>(
    () => ({
      customers,
      quotes,
      projects,
      tasks,
      purchases,
      receivables,
      collections,
      payments,
      projectFinances,
      serviceRecords,
      files,
      notifications,
      calendarEvents,
      loading,
      error,
      refresh: load,
      customerName,
      projectName,
      customerById,
      projectById,
    }),
    [
      customers,
      quotes,
      projects,
      tasks,
      purchases,
      receivables,
      collections,
      payments,
      projectFinances,
      serviceRecords,
      files,
      notifications,
      calendarEvents,
      loading,
      error,
      load,
      customerName,
      projectName,
      customerById,
      projectById,
    ],
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
}